import { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { IBanner } from '../../utils/entities/interface';
import {
  fetchBanners,
  saveBanner,
  editBanner,
  deleteBanner,
} from '../../services/banner';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { ImageUpload } from '../../components/atoms/UploadImage';
import useSnackbar from '../../hooks/useSnackbar';

const DEFAULT_BANNER: IBanner = {
  imageUrl: '',
  destination: 'AD',
  data: '',
  position: 1,
};

const HomeBanner = () => {
  const [banners, setBanners] = useState<IBanner[]>(
    Array(3).fill({ ...DEFAULT_BANNER }),
  );
  const [dirtyFlags, setDirtyFlags] = useState<boolean[]>(Array(3).fill(false)); // Track dirty state for each banner
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<number | null>(null);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const fetchedBanners = await fetchBanners();
      const updatedBanners = [...fetchedBanners];

      while (updatedBanners.length < 3) {
        updatedBanners.push({ ...DEFAULT_BANNER });
      }

      setBanners(updatedBanners.slice(0, 3));
    } catch (error) {
      setError('Error fetching banners');
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerChange = async (
    index: number,
    field: keyof IBanner,
    value: string,
  ) => {
    const updatedBanners: IBanner[] = [...banners];
    updatedBanners[index] = {
      ...updatedBanners[index],
      [field]: value,
      position: index + 1,
    };
    setBanners(updatedBanners);

    // Update the dirty flag for the specific banner
    const updatedDirtyFlags = [...dirtyFlags];
    updatedDirtyFlags[index] = true;
    setDirtyFlags(updatedDirtyFlags);
  };

  const handleRemoveBanner = async (index: number) => {
    try {
      if (banners[index]?._id) {
        await deleteBanner(banners[index]._id as string);
      }
      setBanners((prevBanners) => {
        const newBanners = [...prevBanners];
        newBanners[index] = { ...DEFAULT_BANNER, position: index + 1 };
        return newBanners;
      });

      // Reset the dirty flag for the removed banner
      const updatedDirtyFlags = [...dirtyFlags];
      updatedDirtyFlags[index] = false;
      setDirtyFlags(updatedDirtyFlags);
    } catch (error) {
      setError('Error deleting banner');
      console.error('Error deleting banner:', error);
    }
  };

  const handleSaveChanges = async (index: number) => {
    const banner = banners[index];

    try {
      if (
        !banner.data ||
        !banner.destination ||
        !banner.imageUrl ||
        !banner.position
      ) {
        return showSnackbar('All Fields are Required', 'success');
      }
      setLoading(true);
      if (banner._id) {
        await editBanner({ id: banner._id, ...banner });
      } else {
        const savedBanner = await saveBanner(banner);
        banner._id = savedBanner.toString();
      }

      // Reset the dirty flag after saving
      const updatedDirtyFlags = [...dirtyFlags];
      updatedDirtyFlags[index] = false;
      setDirtyFlags(updatedDirtyFlags);
      loadBanners();
    } catch (error) {
      setError('Error saving banners');
      console.error('Error saving banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (index: number) => {
    setBannerToDelete(index);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBannerToDelete(null);
  };

  return (
    <Box sx={{ padding: 2 }}>
      {SnackbarComponent}
      <Typography variant="h6" gutterBottom>
        Banners Management
      </Typography>
      <Grid container spacing={2}>
        {banners.map((banner, index) => (
          <Grid item xs={12} sm={4} key={banner._id || index}>
            <Card sx={{ padding: 2 }}>
              <CardContent>
                <ImageUpload
                  fileId={`banner-${index}`}
                  type="banner"
                  imageUrl={banner.imageUrl}
                  onChange={(url) => handleBannerChange(index, 'imageUrl', url)}
                  onRemove={() => openDeleteDialog(index)}
                  uploadLabel="Upload Banner"
                  setLoader={setLoading}
                  disabled={loading}
                  folderType={1}
                />
                <FormControl fullWidth margin="dense">
                  <InputLabel>Destination</InputLabel>
                  <Select
                    value={banner.destination}
                    onChange={(e) =>
                      handleBannerChange(
                        index,
                        'destination',
                        e.target.value as 'AD' | 'COUPON',
                      )
                    }
                  >
                    <MenuItem value="AD">Ad</MenuItem>
                    <MenuItem value="COUPON">Coupon</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Data"
                  variant="outlined"
                  margin="dense"
                  value={banner.data}
                  onChange={(e) =>
                    handleBannerChange(index, 'data', e.target.value)
                  }
                />
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  onClick={() => openDeleteDialog(index)}
                  disabled={loading}
                  color="error"
                >
                  Delete
                </Button>
                {dirtyFlags[index] && (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleSaveChanges(index)}
                    disabled={loading}
                  >
                    Save Changes
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          message={error}
        />
      )}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            onClick={() => {
              if (bannerToDelete !== null) handleRemoveBanner(bannerToDelete);
              closeDeleteDialog();
            }}
            color="primary"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(HomeBanner);
